import { useEffect, useState } from 'react'
import { Search } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { FilterDropdown } from '@/components/ui/filter-dropdown'
import { Input } from '@/components/ui/input'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { getJSON } from '@/lib/api'
import { useQuery } from '@tanstack/react-query'

type DirectoryField = {
  key: string
  label: string
  value: string | number | string[] | { thumbnail_url: string }
}
type DirectoryEntry = {
  slug: string
  name: string
  kind?: 'person' | 'organization'
  fields: DirectoryField[]
}
export type DirectoryResponse = {
  member_directory: {
    enabled: boolean
    people: DirectoryEntry[]
    page: number
    page_size: number
    total: number
    filters: Array<{ key: string; label: string; choices: string[] }>
  }
}

export const memberDirectoryQueryKey = ['member-portal-directory'] as const

export function MemberPortalDirectory() {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [selectedFilters, setSelectedFilters] = useState<Record<string, Set<string>>>({})
  const query = useQuery<DirectoryResponse>({
    queryKey: [...memberDirectoryQueryKey, search, page, selectedFilterKey(selectedFilters)],
    queryFn: () => getJSON(directoryPath({ search, page, selectedFilters })),
    retry: false,
  })
  const directory = query.data?.member_directory
  const filterKey = selectedFilterKey(selectedFilters)

  useEffect(() => setPage(1), [search, filterKey])

  const clearFilters = () => {
    setSearch('')
    setSelectedFilters({})
  }

  if (query.isLoading) {
    return (
      <p className="mt-6 text-body-sm text-muted-foreground" role="status">
        Loading Directory…
      </p>
    )
  }
  if (query.isError || !directory) {
    return <Alert className="mt-6">The Directory is unavailable. Please try again.</Alert>
  }

  const hasFilters =
    search.trim() !== '' || Object.values(selectedFilters).some((values) => values.size > 0)
  const noResults = directory.total === 0
  const pages = Math.max(1, Math.ceil(directory.total / directory.page_size))

  return (
    <div className="mt-6 space-y-4">
      <div className="space-y-1">
        <p className="text-body-sm text-muted-foreground">Members of your Association.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full min-w-0 max-w-sm sm:w-auto sm:flex-1">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            aria-label="Search Directory"
            className="h-8 pl-8"
            placeholder="Search name or organization"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        {directory.filters.map((filter) => (
          <FilterDropdown
            key={filter.key}
            label={filter.label}
            emptyLabel={filter.label}
            options={filter.choices.map((choice) => ({ value: choice, label: choice }))}
            selected={selectedFilters[filter.key] ?? new Set()}
            onChange={(values) =>
              setSelectedFilters((current) => ({ ...current, [filter.key]: values }))
            }
          />
        ))}
      </div>
      <div className="flex items-center justify-between">
        <p className="text-caption text-muted-foreground">
          {directory.total} member{directory.total === 1 ? '' : 's'}
        </p>
        <p className="text-caption text-muted-foreground">Sorted by last name</p>
      </div>
      {noResults ? (
        hasFilters ? (
          <EmptyState
            title="No members match your search."
            action={
              <Button type="button" variant="outline" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            }
          />
        ) : (
          <EmptyState title="No members are listed yet." />
        )
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
            {directory.people.map((entry) => (
              <DirectoryCard key={entry.slug} entry={entry} />
            ))}
          </div>
          {pages > 1 && (
            <DirectoryPagination page={directory.page} pages={pages} onPageChange={setPage} />
          )}
        </>
      )}
    </div>
  )
}

function DirectoryCard({ entry }: { entry: DirectoryEntry }) {
  if (entry.kind === 'organization') return <OrganizationCard organization={entry} />

  const photo = entry.fields.find((field) => field.key === 'profile_photo')?.value
  const level = entry.fields.find((field) => field.key === 'membership_level')?.value
  const organizationText = directoryText(entry, 'organization_affiliations')
  const chapterText = directoryText(entry, 'chapters')
  const photoUrl =
    typeof photo === 'object' && photo !== null && !Array.isArray(photo)
      ? photo.thumbnail_url
      : null
  return (
    <Link
      to={`/portal/directory/${entry.slug}`}
      className="block overflow-hidden rounded-md border border-border bg-card transition-colors hover:border-primary/40"
    >
      {photoUrl ? (
        <img src={photoUrl} alt="" className="aspect-square w-full object-cover" />
      ) : (
        <div className="flex aspect-square w-full items-center justify-center bg-muted">
          <span className="text-heading-sm font-medium text-muted-foreground">
            {initials(entry.name)}
          </span>
        </div>
      )}
      <div className="space-y-0.5 p-3 leading-snug">
        {level !== undefined && (
          <p className="text-caption font-medium text-primary">{String(level)}</p>
        )}
        <p className="truncate text-body-sm font-medium text-foreground">{entry.name}</p>
        {organizationText && (
          <p className="truncate text-caption text-muted-foreground">{String(organizationText)}</p>
        )}
        {chapterText && (
          <p className="truncate text-caption text-muted-foreground">{String(chapterText)}</p>
        )}
      </div>
    </Link>
  )
}

function OrganizationCard({ organization }: { organization: DirectoryEntry }) {
  const photo = organization.fields.find((field) => field.key === 'profile_photo')?.value
  const level = organization.fields.find((field) => field.key === 'membership_level')?.value
  const website = organization.fields.find((field) => field.key === 'website')?.value
  const city = organization.fields.find((field) => field.key === 'city')?.value
  const photoUrl =
    typeof photo === 'object' && photo !== null && !Array.isArray(photo)
      ? photo.thumbnail_url
      : null
  const location = [website, city].filter(Boolean).join(' · ')

  return (
    <Link
      to={`/portal/directory/${organization.slug}`}
      className="block overflow-hidden rounded-md border border-border bg-card transition-colors hover:border-primary/40"
    >
      {photoUrl ? (
        <div className="flex aspect-square w-full items-center justify-center bg-muted">
          <img src={photoUrl} alt="" className="h-full w-full object-contain p-6" />
        </div>
      ) : (
        <div className="flex aspect-square w-full items-center justify-center bg-muted">
          <span className="text-heading-sm font-medium text-muted-foreground">
            {initials(organization.name)}
          </span>
        </div>
      )}
      <div className="space-y-0.5 p-3 leading-snug">
        {level !== undefined && (
          <p className="text-caption font-medium text-primary">{String(level)}</p>
        )}
        <p className="truncate text-body-sm font-medium text-foreground">{organization.name}</p>
        {location && <p className="truncate text-caption text-muted-foreground">{location}</p>}
      </div>
    </Link>
  )
}

function DirectoryPagination({
  page,
  pages,
  onPageChange,
}: {
  page: number
  pages: number
  onPageChange: (page: number) => void
}) {
  const visiblePages = paginationPages(page, pages)
  return (
    <Pagination>
      <PaginationContent className="flex-wrap justify-center">
        <PaginationItem>
          <PaginationPrevious
            href="#"
            aria-disabled={page === 1}
            className={page === 1 ? 'pointer-events-none opacity-50' : undefined}
            onClick={(event) => {
              event.preventDefault()
              if (page > 1) onPageChange(page - 1)
            }}
          />
        </PaginationItem>
        {visiblePages.map((number, index) =>
          number === null ? (
            <PaginationItem key={`ellipsis-${index}`}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={number}>
              <PaginationLink
                href="#"
                isActive={number === page}
                onClick={(event) => {
                  event.preventDefault()
                  onPageChange(number)
                }}
              >
                {number}
              </PaginationLink>
            </PaginationItem>
          ),
        )}
        <PaginationItem>
          <PaginationNext
            href="#"
            aria-disabled={page === pages}
            className={page === pages ? 'pointer-events-none opacity-50' : undefined}
            onClick={(event) => {
              event.preventDefault()
              if (page < pages) onPageChange(page + 1)
            }}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  )
}

function paginationPages(page: number, pages: number): Array<number | null> {
  if (pages <= 7) return Array.from({ length: pages }, (_, index) => index + 1)
  const windowStart = Math.max(2, Math.min(page - 1, pages - 3))
  const windowEnd = Math.min(pages - 1, windowStart + 2)
  return [
    1,
    ...(windowStart > 2 ? [null] : []),
    ...Array.from({ length: windowEnd - windowStart + 1 }, (_, index) => windowStart + index),
    ...(windowEnd < pages - 1 ? [null] : []),
    pages,
  ]
}

function directoryPath({
  search,
  page,
  selectedFilters,
}: {
  search: string
  page: number
  selectedFilters: Record<string, Set<string>>
}) {
  const params = new URLSearchParams({ page: String(page) })
  if (search.trim()) params.set('search', search.trim())
  Object.entries(selectedFilters).forEach(([key, values]) =>
    values.forEach((value) => params.append(`filters[${key}]`, value)),
  )
  return `/api/v1/member_directory?${params.toString()}`
}

function selectedFilterKey(filters: Record<string, Set<string>>) {
  return Object.entries(filters)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, values]) => `${key}:${Array.from(values).sort().join(',')}`)
    .join('|')
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

function directoryText(entry: DirectoryEntry, key: string) {
  const value = entry.fields.find((field) => field.key === key)?.value
  return Array.isArray(value) ? value.join(', ') : value
}

export function directoryEnabled(directory: DirectoryResponse | undefined) {
  return directory?.member_directory?.enabled === true
}
